import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal } from '../../../components/common/Modal';
import { purchaseOrderService } from '../../../services/purchaseOrderService';
import { formatCurrency } from '../../../utils/format';
import { useSettingsStore } from '../../../store/settingsStore';
import { CreatePurchaseOrderForm } from '../../purchase-orders/CreatePurchaseOrderForm';
import type { PurchaseOrder } from '../../../types/purchaseOrder';
import { LoadingSpinner } from '../../../components/common/LoadingSpinner';
import { useLanguage } from '../../../features/language/useLanguage';

interface OutstandingPaymentsModalProps {
  isOpen: boolean;
  onClose: () => void;
  startDate: string;
  endDate: string;
}

export const OutstandingPaymentsModal: React.FC<OutstandingPaymentsModalProps> = ({
  isOpen,
  onClose,
  startDate,
  endDate,
}) => {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const { currencySymbol } = useSettingsStore();
  const [payingPO, setPayingPO] = useState<PurchaseOrder | null>(null);

  const { data: pos = [], isLoading } = useQuery<PurchaseOrder[]>({
    queryKey: ['purchase-orders'],
    queryFn: purchaseOrderService.getAll,
    enabled: isOpen, // Only fetch when modal is open
  });

  // Filter for unpaid POs (paymentStatus === 'unpaid')
  const unpaidPOs = pos.filter(po => {
    if ((po.paymentStatus ?? 'unpaid') !== 'unpaid' || po.status === 'cancelled') return false;
    
    const poDate = po.orderDate?.slice(0, 10);
    if (!poDate) return false;
    
    return poDate >= startDate && poDate <= endDate;
  });

  // Sort by date descending (newest first)
  const sortedPOs = [...unpaidPOs].sort((a, b) => 
    new Date(b.orderDate).getTime() - new Date(a.orderDate).getTime()
  );

  const handlePaySuccess = async () => {
    await queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
    // Also invalidate dashboard stats
    await queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
    await queryClient.invalidateQueries({ queryKey: ['inventory-alerts'] });
    setPayingPO(null);
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={t('dashboard.outstandingPaymentsModal.title')}
        maxWidth="5xl"
      >
        <div className="space-y-4">
          {isLoading ? (
            <LoadingSpinner text={t('dashboard.outstandingPaymentsModal.loading')} />
          ) : sortedPOs.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {t('dashboard.outstandingPaymentsModal.empty')}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('forms.common.orderNumber')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('purchasing.sampleList.table.supplier')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('reports.general.table.date')}
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('reports.details.total')}
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('purchaseOrder.table.orderStatus')}
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('purchaseOrder.table.paymentStatus')}
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('purchaseOrder.table.deliveryStatus')}
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('settingsMcp.table.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sortedPOs.map((po) => (
                    <tr key={po.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {po.orderNumber}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {po.supplierName || t('settingsAdminMembers.unknownCompany')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(po.orderDate).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                        {formatCurrency(po.totalAmount, currencySymbol)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full uppercase py-1
                          ${po.status === 'draft' ? 'bg-blue-100 text-blue-800' : 
                            po.status === 'ordered' ? 'bg-green-100 text-green-800' : 
                            'bg-gray-100 text-gray-800'}`}>
                          {t(`purchaseOrder.filters.options.${po.status}`, undefined, po.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full uppercase py-1
                          ${po.paymentStatus === 'paid' ? 'bg-green-100 text-green-800' : 
                            po.paymentStatus === 'refunded' ? 'bg-gray-100 text-gray-800' : 
                            'bg-red-100 text-red-800'}`}>
                          {t(`purchaseOrder.filters.options.${po.paymentStatus || 'unpaid'}`, undefined, po.paymentStatus || 'unpaid')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full uppercase py-1
                          ${po.deliveryStatus === 'received' ? 'bg-green-100 text-green-800' : 
                            po.deliveryStatus === 'shipping' ? 'bg-blue-100 text-blue-800' : 
                            'bg-gray-100 text-gray-800'}`}>
                          {t(`purchaseOrder.filters.options.${po.deliveryStatus || 'pending'}`, undefined, po.deliveryStatus || 'pending')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => setPayingPO(po)}
                          className="text-blue-600 hover:text-blue-900 hover:underline"
                        >
                          {t('dashboard.outstandingPaymentsModal.markAsPaid')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Modal>

      {/* Pay Modal */}
      <Modal
        isOpen={!!payingPO}
        onClose={() => setPayingPO(null)}
        title={t('dashboard.outstandingPaymentsModal.markAsPaidTitle', { orderNumber: payingPO?.orderNumber || '' })}
        className="max-w-4xl"
      >
        {payingPO && (
          <CreatePurchaseOrderForm
            mode="pay"
            initialData={{
              ...payingPO,
              paymentStatus: 'paid'
            }}
            onSubmit={async (data) => {
              await purchaseOrderService.update(
                payingPO.id,
                data,
                payingPO.orderNumber
              );
              handlePaySuccess();
            }}
            onCancel={() => setPayingPO(null)}
          />
        )}
      </Modal>
    </>
  );
};
